import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { addTask } from '../store/slices/taskInfoSlice';
import { addOrgTaskForFirm } from '../store/slices/orgTasksSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch, roundToDeadline, showAlert } from '../utils/utils';

import newTaskFormStyle from '../assets/jss/crm/components/newTaskFormStyle.jsx';

import KSelect from './KSelect';
import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import CrmRadio from './CrmRadio';
import AlertModal from './AlertModal';
import KDatePicker from './KDatePicker';
import DA from '../store/ReduxAccess';

const localStyles = (theme) => ({
  ...newTaskFormStyle(theme),
  subjectSelectorHolder: {
    marginTop: '4px',
  },
  assignementSection: {
    marginTop: '20px',
  },
});

/*
Call this with
    <NewTaskForm
      subjectIdsNamesAndCategories={subjectIdsNamesAndCategories}
      initialSubjectStatus=<'Lead' | 'Prospect' | 'Client' | 'Alum' | 'Referral Company' | 'Referral Contact'>
      initialSubjectId={initialSubjectId}
      visibleFirmId={visibleFirmId}
      closeModalFunc={closeModalFunc}
    />
*/

class NewTaskForm extends Component {
  constructor(props) {
    super(props);

    const today = DateTime.local().startOf('day');

    this.createCategoryMaps();
    this.computeFixedOptions();

    this.state = {
      redirect: null,
      description: '',
      chosenCategory: this.storedStatusToDisplayedCategoryMap[this.props.initialSubjectStatus] || '',
      subjectId: this.props.initialSubjectId || 0,
      assigneeCategory: 'person',
      assigneeEmail: 0,
      assignedGroupId: 0,
      launchNowOrLater: 'now',
      assignmentDeadline: today,
      acceptanceDeadline: today,
      completionDeadline: today,
      groupNameOptions: [],
      groupIdOptions: [],
    };

    this.activationDate = today;
  }

  categoryOptions = ['Leads', 'Prospects', 'Active Clients', 'Alumni', 'Referral Companies', 'Referral Contacts'];
  subjectOptions = [];
  userNameOptions = [];
  userEmailOptions = [];

  createCategoryMaps() {
    this.displayedCategoryToStoredStatusMap = {
      Leads: 'Lead',
      Prospects: 'Prospect',
      'Active Clients': 'Client',
      Alumni: 'Alum',
      'Referral Companies': 'Referral Company',
      'Referral Contacts': 'Referral Contact',
    };

    this.storedStatusToDisplayedCategoryMap = {
      Lead: 'Leads',
      Prospect: 'Prospects',
      Client: 'Active Clients',
      Alum: 'Alumni',
      'Referral Company': 'Referral Companies',
      'Referral Contactss': 'Referral Contacts',
    };
  }

  freshenState() {
    const today = DateTime.local().startOf('day');
    const freshState = {
      redirect: null,
      description: '',
      chosenCategory: this.storedStatusToDisplayedCategoryMap[this.props.initialSubjectStatus] || '',
      subjectId: this.props.initialSubjectId || 0,
      assigneeCategory: 'person',
      assigneeEmail: 0,
      assignedGroupId: 0,
      launchNowOrLater: 'now',
      assignmentDeadline: today,
      acceptanceDeadline: today,
      completionDeadline: today,
    };
    this.setState(freshState);
  }

  computeFixedOptions() {
    const users = DA.getAllFirmUsers();
    this.userNameOptions = users.map((u) => u.name);
    this.userEmailOptions = users.map((u) => u.email);
  }

  componentDidMount() {
    this.computeOptionLists();
    if (this.state.chosenCategory) {
      const displayedCategory = this.storedStatusToDisplayedCategoryMap[this.props.initialSubjectStatus];
      this.applySubjectFilter(displayedCategory);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.subjectIdsNamesAndCategories !== prevProps.subjectIdsNamesAndCategories) {
      this.computeOptionLists();
    }
  }

  computeOptionLists() {
    const groups = this.props.taskInfo.taskableGroups || [];
    this.setState({ groupNameOptions: groups.map((g) => g.name) });
    this.setState({ groupIdOptions: groups.map((g) => g.id) });

    this.preselectSomeOptions();
  }

  preselectSomeOptions() {
    if (this.props.initialSubjectId) {
      this.setState({ subjectId: this.props.initialSubjectId });
    } else if (this.subjectOptions.length === 1) {
      const singleSubject = this.subjectOptions[0];
      this.setState({ subjectId: singleSubject.id });
    }
    this.setState({ assigneeEmail: this.props.userInfo.email });
    if (this.state.groupIdOptions.length === 1) {
      this.setState({ assignedGroupId: this.state.groupIdOptions[0] });
    }
  }

  handleSelectSubjectType(newCategory) {
    this.setState({ subjectId: 0, chosenCategory: newCategory });
    this.applySubjectFilter(newCategory);
  }

  getSelectedSubjectType() {
    return this.state.chosenCategory;
  }

  getSelectedSubject() {
    const { subjectId, chosenCategory } = this.state;
    const chosenStatus = this.displayedCategoryToStoredStatusMap[chosenCategory];
    const optionWithSameId = this.subjectOptions.find((opt) => opt.id === subjectId && opt.status == chosenStatus);
    return optionWithSameId || { id: 0, name: '' };
  }

  handleSelectSubject(subject) {
    const newSubjectId = subject ? subject.id : 0;
    this.setState({ subjectId: newSubjectId });
  }

  applySubjectFilter(subjectCategory) {
    const subjects = this.props.subjectIdsNamesAndCategories || [];
    const statusToFilterBy = this.displayedCategoryToStoredStatusMap[subjectCategory];
    const filteredSubjects = subjects.filter((s) => s && s.status === statusToFilterBy);
    this.subjectOptions = filteredSubjects.sort((a, b) =>
      a.name.toLowerCase().trim() > b.name.toLowerCase().trim() ? 1 : -1
    );
    if (filteredSubjects.length == 1) {
      this.setState({ subjectId: filteredSubjects[0].id });
    } else {
      this.setState({ subjectId: 0 });
    }
  }

  handleAssignmentTypeChange(event) {
    this.setState({ assigneeCategory: event.target.value });
  }

  setAssigneeId(newValue) {
    if (this.state.assigneeCategory === 'person') {
      this.setState({ assigneeEmail: newValue });
    } else {
      this.setState({ assignedGroupId: newValue });
    }
  }

  selectControlForAssignmentType(classes) {
    switch (this.state.assigneeCategory) {
      case 'person':
        return (
          <KSelect
            fullWidth
            label="Assignee"
            liveOptions={this.userNameOptions}
            liveValues={this.userEmailOptions}
            value={this.state.assigneeEmail}
            onChange={(event) => this.setAssigneeId(event.target.value)}
          />
        );
      case 'group':
        if (this.state.groupNameOptions.length === 0) {
          return null;
        } else {
          return (
            <KSelect
              fullWidth
              label="Assigned Group"
              liveOptions={this.state.groupNameOptions}
              liveValues={this.state.groupIdOptions}
              value={
                this.state.assignedGroupId === 0
                  ? this.setState({ assignedGroupId: this.state.groupIdOptions[0] })
                  : this.state.assignedGroupId
              }
              onChange={(event) => this.setAssigneeId(event.target.value)}
            />
          );
        }
      case 'no-one':
      default:
        return <KSelect className={classes.hiddenAssignmentSelect} placeholder="This shouldn't be visible" />;
    }
  }

  changeAssigmentDeadline(newDate) {
    const roundedDate = roundToDeadline(newDate);
    this.setState({ assignmentDeadline: roundedDate });
    this.changeAcceptanceDeadline(roundedDate);
  }

  changeAcceptanceDeadline(newDate) {
    const roundedDate = roundToDeadline(newDate);
    this.setState({ acceptanceDeadline: roundedDate });
    this.changeCompletionDeadline(roundedDate);
  }

  changeCompletionDeadline(newDate) {
    const roundedDate = roundToDeadline(newDate);
    this.setState({ completionDeadline: roundedDate });
  }

  cancelModal() {
    this.clearForm();
    this.props.closeModalFunc();
  }

  clearForm() {
    this.freshenState();
  }

  acceptForm() {
    const validation = this.validateForm();
    if (validation.valid) {
      this.createApiStructAndCallCreateTaskApi();
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  createApiStructAndCallCreateTaskApi() {
    const uri = '/api/task';
    const apiStruct = this.buildNewTaskApiStruct();
    const request = createStdRequest(uri, 'post', apiStruct);
    const actionOnGoodResponse = (json) => {
      const task = json.task;

      this.addNewTaskToReduxStore(task);
      const visibleFirmId = this.props.visibleFirmId;
      if (visibleFirmId) {
        this.props.addOrgTaskForFirm({ visibleFirmId, task });
      }
      this.clearForm();
      this.props.closeModalFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildNewTaskApiStruct() {
    const taskId = 0;
    const description = this.state.description.trim();
    const activationDate = this.getDateForLaunchTime();
    const ownerEmail = this.props.userInfo.email;
    const assigneeEmail = this.state.assigneeCategory === 'person' ? this.state.assigneeEmail : '';
    const assigneeGroupId = this.state.assigneeCategory === 'group' ? this.state.assignedGroupId : 0;
    const currentStateName =
      ownerEmail === assigneeEmail ? 'Cliamed' : this.state.assigneeCategory === 'no-one' ? 'Unassigned' : 'Assigned';
    const claimaintEmail = ownerEmail === assigneeEmail ? assigneeEmail : '';
    let subjectId = this.state.subjectId;
    let subjectFirmId = this.props.visibleFirmId || 0;
    let inquiryId = 0;
    const possiblyModifiedAssignmentDeadline =
      this.state.launchNowOrLater === 'later' ? this.state.assignmentDeadline : this.activationDate;

    switch (this.state.chosenCategory) {
      case 'Leads': // for inquiry
        inquiryId = subjectId;
        subjectId = 0;
        break;
      case 'Referral Companies':
        subjectFirmId = subjectId;
        subjectId = 0;
        break;
      case 'Referral Contacts':
        const contact = DA.getPersonById(subjectId);
        subjectFirmId = contact.referralFirmId;
        break;
      default:
        break;
    }

    return {
      taskId,
      description,
      activationDate,
      ownerEmail,
      assigneeEmail,
      assigneeGroupId,
      currentStateName,
      claimaintEmail,
      subjectId,
      subjectFirmId,
      inquiryId,
      assignmentDeadline: possiblyModifiedAssignmentDeadline.toISO(),
      acceptanceDeadline: this.state.acceptanceDeadline.toISO(),
      completionDeadline: this.state.completionDeadline.toISO(),
    };
  }

  addNewTaskToReduxStore(taskStruct) {
    // taskStruct is in the same format as the entries in this.props.taskInfo.tasks
    // Let's just call an action creator and rely on the reducer to do the right thing
    this.props.addTask(taskStruct);
  }

  getDateForLaunchTime() {
    if (this.state.launchNowOrLater === 'now') {
      return DateTime.local();
    } else {
      return this.activationDate;
    }
  }

  validateForm() {
    let validation = this.validateDescription();
    if (!validation.valid) {
      return validation;
    }
    validation = this.validateAssignmentSelection();
    if (!validation.valid) {
      return validation;
    }
    return this.validateDates();
  }

  validateDescription() {
    return this.state.description.trim()
      ? { valid: true }
      : { valid: false, errorMessage: 'You need to provide a description' };
  }

  validateAssignmentSelection() {
    const { assigneeCategory, assigneeEmail, assignedGroupId } = this.state;
    if (assigneeCategory === 'person' && assigneeEmail === '') {
      return { valid: false, errorMessage: 'You need to select a person to whom you are assigning this task' };
    } else if (assigneeCategory === 'group' && (assignedGroupId === 0 || assignedGroupId === '0')) {
      return { valid: false, errorMessage: 'You need to select a group to which you are assigning this task' };
    } else {
      return { valid: true };
    }
  }

  validateDates() {
    const { assigneeCategory, assignmentDeadline, acceptanceDeadline, completionDeadline } = this.state;
    if (assigneeCategory === 'no-one') {
      if (acceptanceDeadline.valueOf() < assignmentDeadline.valueOf()) {
        const errMsg = "Your Acceptance Deadline can't precede your Assignment Deadline";
        return { valid: false, errorMessage: errMsg };
      }
    }
    if (completionDeadline.valueOf() < acceptanceDeadline.valueOf()) {
      const errMsg = "Your Completion Deadline can't precede your Acceptance Deadline";
      return { valid: false, errorMessage: errMsg };
    }
    return { valid: true };
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes } = this.props;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CrmTextField
              label="Description"
              fullWidth
              multiline
              value={this.state.description}
              onChange={(event) => this.setState({ description: event.target.value })}
            />
          </Grid>
          <Grid item className={classes.subjectSelectorHolder} xs={6}>
            <Autocomplete
              options={this.categoryOptions}
              getOptionLabel={(option) => option}
              value={this.state.chosenCategory}
              renderInput={(params) => (
                <TextField {...params} label="Client/Subject Type (Optional)" variant="standard" />
              )}
              onChange={(event, newCategory) => this.handleSelectSubjectType(newCategory)}
            />
          </Grid>

          <Grid item className={classes.subjectSelectorHolder} xs={6}>
            <Autocomplete
              options={this.subjectOptions}
              getOptionLabel={(option) => option.name}
              value={this.getSelectedSubject()}
              renderInput={(params) => (
                <TextField {...params} label="Client/Subject Name (Optional)" variant="standard" />
              )}
              onChange={(event, subject) => this.handleSelectSubject(subject)}
            />
          </Grid>

          <Grid item className={classes.assignementSection} xs={12}>
            Assign to
            <Grid item xs={1} />
            <Grid item xs={11}>
              <FormControl variant="standard" component="fieldset" className={classes.formControl}>
                <RadioGroup
                  row
                  aria-label="Assignment Type"
                  name="assignment-type"
                  className={classes.group}
                  value={this.state.assigneeCategory}
                  onChange={(event) => this.handleAssignmentTypeChange(event)}
                >
                  <FormControlLabel value="person" control={<CrmRadio />} label="person" />
                  {this.state.groupNameOptions.length > 0 && (
                    <FormControlLabel value="group" control={<CrmRadio />} label="group" />
                  )}
                  <FormControlLabel value="no-one" control={<CrmRadio />} label="unassigned" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Collapse in={this.state.assigneeCategory !== 'no-one'} timeout={400}>
              <Grid item xs={1} />
              <Grid item xs={12}>
                {this.selectControlForAssignmentType(classes)}
              </Grid>
            </Collapse>
          </Grid>

          <Grid item xs={6}>
            <div className={classes.datePickerDiv}>
              <KDatePicker
                label="Due Date"
                value={this.state.completionDeadline}
                onChange={(newDate) => this.changeAssigmentDeadline(newDate)}
              />
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className={classes.buttonDiv}>
              <CrmButton outlined onClick={() => this.cancelModal()}>
                Cancel
              </CrmButton>
              <CrmButton outlined onClick={() => this.clearForm()}>
                Clear
              </CrmButton>
              <CrmButton filled onClick={() => this.acceptForm()}>
                Save
              </CrmButton>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  taskInfo: state.taskInfo,
});

const actionProducers = { addTask, addOrgTaskForFirm, setApiResponseInfo };

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(NewTaskForm));
